import { getMediaPlaybackRate, shouldLoopMedia } from '../../common';
import { roundToPrecision } from '../../libs/frameUtils';
import { assertMinimum, assertMaximum, assertNumber } from '../../validators';
import type { DestroyOptions } from '../../types';
import { Config } from '../../video-data/config';
import { MorphManager, type MorphableProperty } from '../morph/MorphManager';
import { MediaAsset } from './MediaAsset';

/**
 * The TimeAwareMediaAsset extends the MediaAsset object, adding in support for time-sensitive properties. These
 * notably are useful for video assets, including startTime, endTime, and volume.
 */
export class TimeAwareMediaAsset extends MediaAsset {
    override _classType = 'TimeAwareMediaAsset'; // should match VideoData.MEDIA_ASSET_TYPE

    // animation
    _loopPlayback = () => this.loopPlayback();

    /**
     * Here we're overriding the MediaAsset sourceData property so we automatically reset
     * properties which are directly related to the sourceData.
     * - the startTime & endTime
     * - the volume (if the new media no longer has audio)
     */
    override set sourceData(sourceData) {
        super.sourceData = sourceData;

        if (!this.hasAudio) {
            this.volume = 0;
        }

        if (this.#playbackRateOverride !== null) {
            this.#playbackRateOverride = null;
            this.requiresRedraw = true;
        }
    }

    override get sourceData() {
        return super.sourceData;
    }

    set volume(value: number) {
        if (this.mediaAssetType === Config.IMAGE_TYPE_BACKGROUND && value > 0) {
            throw new Error('Cannot set volume for an image.');
        }
        this._audioAsset.volume = value;
    }

    get volume(): number {
        if (this.linkedAsset) {
            return this.linkedAsset.volume;
        }
        return this._audioAsset.volume;
    }

    /**
     * Calculates the relative time for a time aware media asset. This is where we deal with looping and playback
     * rate.
     */
    override timeRelativeTo(time: number) {
        if (!this.isVideo) {
            return time;
        }

        const parentDuration = this.linkToSceneGroup
            ? this.slide.sceneGroup.duration
            : this.luminaryParent.duration;
        const mediaDuration = this.duration / this.playbackRate;
        if (parentDuration === -1 || mediaDuration === -1) {
            return 0;
        }

        let timeMS = roundToPrecision(time, Config.VIDEO_TIME_PRECISION_MS);
        if (timeMS > parentDuration) {
            timeMS = parentDuration;
        }

        let timeOffsetMS: number;
        if (shouldLoopMedia(parentDuration, mediaDuration, this.volume)) {
            timeOffsetMS = timeMS % mediaDuration;
        } else {
            timeOffsetMS = timeMS * this.playbackRate;
        }

        const morphOffsetMS = MorphManager.getMediaTimeOffset(this.id) || 0;
        const finalTime = (this.startTime + timeOffsetMS + morphOffsetMS) / 1000;
        return roundToPrecision(finalTime, Config.VIDEO_TIME_PRECISION);
    }

    #playbackRate: number = 1.0;
    #playbackRateOverride: number | null = null;

    get playbackRate(): number {
        return this.#playbackRate;
    }

    set playbackRate(value: number) {
        if (this.#playbackRateOverride === value) {
            return;
        }
        if (!this.isVideo) {
            return;
        }

        assertNumber(value);
        assertMinimum(value, 0.0625);
        assertMaximum(value, 16);

        this.#playbackRateOverride = value;
        this.requiresRedraw = true;
    }

    get isPlaybackRateOverridden(): boolean {
        return this.#playbackRateOverride !== null;
    }

    _playbackRateOverrideCustomSave() {
        return this.#playbackRateOverride;
    }

    _playbackRateOverrideCustomLoad(value: number) {
        this.#playbackRateOverride = value;
    }

    _checkAndFixPlaybackRate() {
        if (!this.isVideo) {
            return;
        }

        if (this.#playbackRateOverride === null) {
            /**
             * Sets the calculated playback rate.
             * If the media is less than 33% shorter than the parent's
             * duration then we're slowing down the video by setting the playbackRate below 1.0. If the media is more than 33%
             * shorter, then we switch to looping and the playbackRate is 1.0.
             */
            this.#playbackRate = getMediaPlaybackRate(
                this.luminaryParent.duration,
                this.duration,
                this.volume,
            );
        } else {
            this.#playbackRate = this.#playbackRateOverride;
        }

        if (!this.isLoaded || !this.textureSourceDomElement) {
            return;
        }
        (this.textureSourceDomElement as HTMLVideoElement).playbackRate = this.#playbackRate;
    }

    _checkAndFixLooping() {
        // clear any callbacks we might have
        this.luminaryParent._timeline.removeCallback(this._loopPlayback);

        // add callbacks to loop if necessary
        if (!this.isVideo || !this.isLoaded) {
            return;
        }
        if (this.luminaryParent.duration === -1 || this.duration === -1) {
            return;
        }
        let media_duration = this.duration / this.playbackRate;
        if (!shouldLoopMedia(this.luminaryParent.duration, media_duration, this.volume)) {
            return;
        }
        if (media_duration > 0 && this.luminaryParent.duration > 0) {
            while (media_duration < this.luminaryParent.duration) {
                this.luminaryParent._timeline.addCallback(this._loopPlayback, media_duration);
                media_duration += this.duration;
            }
        }
    }

    loopPlayback() {
        if (!this.luminaryParent || !this.isVideo) {
            return;
        }
        if (this.linkToSceneGroup) {
            return;
        }
        /**
         * cuts are currently only available on voice driven scene groups
         * and they don't allow looping
         */
        if (this.playbackRegions.length > 1) {
            return;
        }
        (this.textureSourceDomElement as HTMLVideoElement).currentTime = this.timeRelativeTo(
            this.luminaryParent.currentTime,
        );
        if (
            this.luminaryParent.isPlaying &&
            (this.textureSourceDomElement as HTMLVideoElement).paused
        ) {
            (this.textureSourceDomElement as HTMLVideoElement).play().catch((e) => {
                // we should wrap native error explicitly in order to extend error with callstack
                throw new Error(e);
            });
        }
    }

    override onLoaded() {
        if (this.isLoaded) {
            const initialAutoDraw = this.autoDraw;
            this.autoDraw = false;
            super.onLoaded();
            this.requiresRedraw = true;
            this.autoDraw = initialAutoDraw;
        }
    }

    override destroy(options?: DestroyOptions) {
        super.destroy(options);
        this.clearAnimation();
    }

    override _checkAndFixProperties() {
        super._checkAndFixProperties();

        this._checkAndFixPlaybackRate();
        this._checkAndFixLooping();
    }

    override _build() {
        // clear
        this.clearAnimation();
        this.resetTransformation();

        // build
        this._loadSourceWithNetworkManager();
        this._updatePlaybackRegionSources();
        this.resizeMedia();

        // dynamic style
        if (this.hasDynamicStyle || this.hasMorphAnimation) {
            this._prepareDynamicStylingForUpdate();
            this.applyTransformation();
            this._applyAnimation();
            this._updateDynamicStyling();
        } else {
            this.applyTransformation();
            this._applyAnimation();
        }

        // post-process animation
        this._applyIntroAnimations();
        this._applyOutroAnimations();
        this.animationManager.setInitValues();
    }

    override get editableProperties() {
        return super.editableProperties.concat(['volume', 'playbackRateOverride']);
    }

    override get morphExcludedProperties(): MorphableProperty[] {
        return ['alpha'];
    }
}
